import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import Login from '../pages/Login/Login';

interface AuthGuardProps {
    children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
    const { user, loading, isAdmin } = useAuth();
    const [isInitialCheck, setIsInitialCheck] = useState(true);

    // Only show loading screen on initial auth check (checking stored token)
    // Not during active login attempts (user is on login page)
    useEffect(() => {
        // After first render, we're no longer doing initial check
        const timer = setTimeout(() => {
            setIsInitialCheck(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    // Show loading only during initial auth check, not during login attempts
    if (isInitialCheck && loading && !user) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    gap: 2,
                }}
            >
                <CircularProgress size={60} />
                <Typography variant="h6" color="textSecondary">
                    Loading Wellness Admin Panel...
                </Typography>
            </Box>
        );
    }

    // Always show Login component when no user, even during login attempts
    if (!user) {
        return <Login />;
    }

    if (!isAdmin) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    p: 3,
                }}
            >
                <Alert severity="error" sx={{ mb: 3, maxWidth: 500 }}>
                    <Typography variant="h6" gutterBottom>
                        Access Denied
                    </Typography>
                    <Typography>
                        You don't have administrator privileges to access this dashboard.
                        Please contact your system administrator.
                    </Typography>
                </Alert>
            </Box>
        );
    }

    return <>{children}</>;
};

export default AuthGuard;
